import { useCallback, useEffect, useState } from 'react';
import type {
  ScheduleConstraint,
  ScheduleConstraintCategory,
  ScheduleConstraintWeight,
} from 'services/api/schedule-configuration';
import type { ConstraintScoreField, UseConstraintWeightsModalParams } from './types';

export const useConstraintWeightsModal = ({
  open,
  onClose,
  onSave,
  weights,
  readOnly,
  logicalConstraints,
  sanpinConstraints,
}: UseConstraintWeightsModalParams) => {
  const [draft, setDraft] = useState<ScheduleConstraintWeight[]>([]);

  const constraintGroups = [
    { category: 'LOGICAL' as const, constraints: logicalConstraints },
    { category: 'SANPIN' as const, constraints: sanpinConstraints },
  ];

  useEffect(() => {
    if (open) setDraft(weights);
  }, [open, weights]);

  const getScore = useCallback(
    (code: ScheduleConstraint['code'], field: ConstraintScoreField) =>
      draft.find((weight) => weight.code === code)?.[field] ?? 0,
    [draft],
  );

  const isInputDisabled = useCallback(
    (constraint: ScheduleConstraint, category: ScheduleConstraintCategory) =>
      readOnly || (category === 'SANPIN' && constraint.mode !== 'ENABLED'),
    [readOnly],
  );

  const handleScoreChange = useCallback(
    (
      constraint: ScheduleConstraint,
      category: ScheduleConstraintCategory,
      field: ConstraintScoreField,
      value: number | null,
    ) => {
      const nextValue = value ?? 0;

      setDraft((current) => {
        const existing = current.find((weight) => weight.code === constraint.code);
        const next: ScheduleConstraintWeight = {
          code: constraint.code,
          category,
          hardScore: existing?.hardScore ?? 0,
          softScore: existing?.softScore ?? 0,
          [field]: nextValue,
        };
        const withoutCurrent = current.filter((weight) => weight.code !== constraint.code);

        return next.hardScore === 0 && next.softScore === 0
          ? withoutCurrent
          : [...withoutCurrent, next];
      });
    },
    [],
  );

  const handleSave = useCallback(() => {
    onSave(draft);
    onClose();
  }, [draft, onClose, onSave]);

  return {
    state: { readOnly, constraintGroups },
    methods: {
      getScore,
      isInputDisabled,
      handleScoreChange,
      handleSave,
      handleClose: onClose,
    },
  };
};
